import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import {
  FolderOpen,
  LayoutGrid,
  Loader2,
  X,
  Save,
  Clock,
} from 'lucide-react'
import { filesApi, type FileListItem } from '@papan/files/client'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass, useConflict, useConfirm } from '@papan/core-ui/ui'

import { getRootFolderId } from './driveRoot'
import { damagedMessageFrom } from '@papan/types'
import {
  PickerNavigator,
  Tooltip,
  crumbsText,
  type PickerNavigatorHandle,
  type PickerCrumb,
  type PickerRoot,
  type PickerSelection,
} from '@papan/files/ui/picker'
import { errorText } from '@papan/api-client'

/** Permissions that grant write capability — used for the read-only badges
 *  and the Save gate. */
const WRITE_PERMS = ['readwrite', 'admin', 'owner']

// Recent export folder — scoped by tenantId
function getRecentExportFolder(tenantId: string): { folderId: string; folderName: string } | null {
  try {
    const raw = localStorage.getItem(`papan:export-recent-folder:${tenantId}`)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function setRecentExportFolder(tenantId: string, folderId: string, folderName: string) {
  localStorage.setItem(`papan:export-recent-folder:${tenantId}`, JSON.stringify({ folderId, folderName }))
}

interface SaveAsDialogProps {
  tenantId: string
  /** Source file ID to copy (not required when customSave is provided) */
  fileId?: string
  /** Default filename (pre-filled in the input) */
  defaultName: string
  /** Default folder ID to pre-select (file's parent). null = root. */
  defaultFolderId?: string | null
  onSaved: (newFileId: string) => void
  onClose: () => void
  /**
   * #929/#930 — asked right before the copy: non-null = the source editor is COVERED (a version preview, the conflict
   * "Saved version" panel) and the save is refused with this message. Save As copies the LIVE document, which the
   * user is not looking at — and a cover can arrive while the dialog is open.
   */
  saveBlockedReason?: () => string | null
  /** Custom save implementation — overrides default filesApi.saveAs behavior */
  /** `keepBoth` false onto an existing name = Replace (a new version of that file). */
  customSave?: (destParentId: string | null, destName: string, keepBoth: boolean) => Promise<{ fileId: string; name: string }>
  /** Dialog title override (default: "Save As") */
  title?: string
  /** Workspace folder ID (.workpapan) for shortcut button */
  workspaceFolderId?: string | null
  /** Workspace name for shortcut label */
  workspaceName?: string
  /** Names the shortcut after the container you are in, the way the
   *  explorer's root section and the menu bar already do. */
  entityKind?: 'project' | 'miniapp'
  /** Custom label for the location section (default: "Shortcut"). Can be a function for dynamic labels. */
  locationLabel?: string | ((fileExists: boolean) => string)
  /** Custom label for the confirm button (default: "Save"). Can be a function for dynamic labels. */
  confirmLabel?: string | ((fileExists: boolean) => string)
  /** Description text shown below the header */
  description?: string
  /** Skip conflict check — used when "existing file" is intentional (e.g. link sheet) */
  skipConflictCheck?: boolean
}

/**
 * Save As / export-to-drive dialog. Single-column drilldown navigator
 * (issue #21) for the destination, with the filename input above it and
 * the recent/source/workspace shortcuts in the navigator's toolbar slot.
 * All the harder behavior — read-only detection + auto-jump to a writable
 * folder, extension-mismatch confirm, conflict resolution, recent-folder
 * memory — lives here in the wrapper and operates on `selectedFolderId` +
 * `fileName`.
 */
export function SaveAsDialog({ tenantId, fileId, defaultName, defaultFolderId, onSaved, onClose, saveBlockedReason, customSave, title, workspaceFolderId, workspaceName: _workspaceName, entityKind, locationLabel, confirmLabel, description, skipConflictCheck }: SaveAsDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const resolveConflict = useConflict()
  const confirm = useConfirm()
  const rootId = getRootFolderId(tenantId)

  // Where the dialog initially opens: most recent save > the file's parent
  // folder (`defaultFolderId`; null means root) > the drive root.
  const recent = getRecentExportFolder(tenantId)
  const effectiveDefaultId = recent?.folderId ?? defaultFolderId ?? rootId
  // "Source Folder" shortcut — a one-click jump to the drive root, always.
  // The file's own folder is already where the dialog opens by default
  // (via `defaultFolderId`), and "This Project" covers the workspace
  // case, so the third shortcut is the top-of-drive escape hatch.
  const sourceFolderId = rootId

  const navigatorRef = useRef<PickerNavigatorHandle>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [selectedFolderPath, setSelectedFolderPath] = useState<string | null>(null)
  const [fileName, setFileName] = useState(defaultName)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [folderWritable, setFolderWritable] = useState<boolean>(true)
  const [selectedFileReadOnly, setSelectedFileReadOnly] = useState<boolean>(false)
  const [writableSuggestion, setWritableSuggestion] = useState<{ id: string; name?: string; parentId?: string | null } | null>(null)
  // Auto-jump to a writable folder runs at most once, on the initial open.
  const autoJumpedRef = useRef(false)

  // The save destination must track the *visibly* selected folder
  // synchronously. `selectedFolderId` is updated via PickerNavigator's
  // `onNavigate`, which fires only AFTER the folder's list fetch resolves —
  // so on a slow/contended backend it lags the breadcrumb, and a Save
  // issued in that window writes to the previously-loaded folder (the
  // save-as-216 repro: the file landed in the source folder, not the
  // picked one). At confirm we read the navigator's synchronous
  // `currentFolderId` instead. The single case where the destination is
  // NOT the browsed folder is a split-view parent-pane file pick — record
  // that intent here, where `handleSelect` runs synchronously (unlike
  // `onNavigate`).
  // `atFolderReported`: whether that folder had already been reported loaded at
  // the pick — so its report can tell a re-opening from the answer to a load
  // the pick was made during.
  const pickedFileRef = useRef<{ parent: string | null; atFolder: string | null; atFolderReported: boolean } | null>(null)

  // Which "Save to" shortcut the user activated. Two shortcuts can point at
  // the same folder (e.g. Source Folder === Last Folder), so the highlight
  // is keyed on *which button was clicked*, not just `selectedFolderId` —
  // otherwise both would light up. Seeded from where the dialog opens.
  const [activeShortcut, setActiveShortcut] = useState<'workspace' | 'source' | 'recent' | null>(() => {
    if (recent && effectiveDefaultId === recent.folderId) return 'recent'
    if (workspaceFolderId && effectiveDefaultId === workspaceFolderId) return 'workspace'
    if (effectiveDefaultId === sourceFolderId) return 'source'
    return null
  })

  const roots = useMemo<PickerRoot[]>(() => [{ id: rootId, label: 'papan' }], [rootId])

  // Auto-focus filename input on mount (the navigator loads independently;
  // its search box autofocus is disabled so focus stays here).
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      const dotIdx = defaultName.lastIndexOf('.')
      inputRef.current.setSelectionRange(0, dotIdx > 0 ? dotIdx : defaultName.length)
    }
    // Mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Navigator callbacks ──────────────────────────────────────────────

  // Fired after the navigator opens a folder. The open folder is the save
  // destination; derive the path + writability from here.
  const handleNavigate = useCallback((folderId: string | null, breadcrumbs: PickerCrumb[], parentPermission?: string) => {
    // A (re)opened folder is the destination again: a file picked before no
    // longer steers it — unless the pick was made while THIS folder was still
    // loading, and this is its first report: the pick still decides.
    const pick = pickedFileRef.current
    const pickStands = folderId !== null && pick?.atFolder === folderId && !pick.atFolderReported && pick.parent !== folderId
    pickedFileRef.current = pickStands && pick ? { ...pick, atFolderReported: true } : null
    if (folderId === null) {
      // A list place (Projects, Shared) is no folder: nothing to save into
      // until an entry is opened, so Save stays disabled meanwhile.
      setSelectedId(null)
      setSelectedFolderId(null)
      setSelectedFolderPath(null)
      setWritableSuggestion(null)
      // …and no read-only warning left over from where it came from.
      setFolderWritable(true)
      setSelectedFileReadOnly(false)
      return
    }
    // The pick decides the destination (the picked file's folder), so this
    // folder's own writability does not gate Save — and the first-open jump to
    // a writable folder is spent, not saved for later.
    if (pickStands) {
      autoJumpedRef.current = true
      setFolderWritable(true)
      setWritableSuggestion(null)
      return
    }
    setSelectedId(folderId)
    setSelectedFolderId(folderId)
    setSelectedFolderPath(crumbsText(breadcrumbs))
    setSelectedFileReadOnly(false)

    const writable = !parentPermission || WRITE_PERMS.includes(parentPermission)
    setFolderWritable(writable)

    if (writable) {
      setWritableSuggestion(null)
      autoJumpedRef.current = true
      return
    }
    // Read-only destination — surface a writable suggestion; on the very
    // first navigation (initial open) also auto-jump there.
    const isFirst = !autoJumpedRef.current
    autoJumpedRef.current = true
    filesApi.listChildren(folderId, { limit: 1, findWritable: true })
      .then(res => {
        const w = res.writableFolders?.[0]
        setWritableSuggestion(w ?? null)
        if (w && isFirst) navigatorRef.current?.navigateTo(w.parentId || w.id)
      })
      .catch(() => setWritableSuggestion(null))
  }, [])

  // Fired when a file row is clicked — copy its name into the input.
  const handleSelect = useCallback((sel: PickerSelection) => {
    if (sel.isFolder) return // folders drill in; onSelect only fires for files
    // Record the pick synchronously (handleConfirm reads this to honour a
    // split-view parent-pane file pick over the browsed folder).
    const atFolder = navigatorRef.current?.currentFolderId ?? null
    pickedFileRef.current = {
      parent: sel.parentId ?? null,
      atFolder,
      atFolderReported: atFolder !== null && navigatorRef.current?.loadedFolderId === atFolder,
    }
    setSelectedId(sel.id)
    setFileName(sel.name)
    if (sel.parentId && sel.parentId !== atFolder) {
      // Picked beside the browsed folder (the parent pane): that is where Save
      // goes, so the browsed folder's read-only state is not the gate — the
      // picked file's own permission (below) is.
      setFolderWritable(true)
      setWritableSuggestion(null)
    }
    // A file can be read-only via its own permission even in a writable folder.
    const perm = sel.raw.permission ?? sel.raw.effectivePermission
    setSelectedFileReadOnly(!!perm && !WRITE_PERMS.includes(perm))
    // Common case: the file lives in the folder we navigated into, so the
    // destination + its displayed path are already correct. Only when a
    // file is picked from the split view's parent pane does the
    // destination differ — resync the folder id and its path there.
    if (sel.parentId && sel.parentId !== selectedFolderId) {
      setSelectedFolderId(sel.parentId)
      // Told as the navigator tells it, so it reads the same as the path above.
      navigatorRef.current?.pathOf(sel.parentId)
        .then(path => setSelectedFolderPath(crumbsText(path)))
        .catch(() => { /* keep the previous path on failure */ })
    }
  }, [selectedFolderId])

  // Read-only lock badge on rows in the list.
  const decorateItem = useCallback((item: FileListItem, parentPermission?: string) => {
    const perm = item.permission ?? item.effectivePermission ?? parentPermission
    return { readOnly: !!perm && !WRITE_PERMS.includes(perm) }
  }, [])

  // Track if the current filename exists in the selected folder (for dynamic labels)
  const [fileExistsInFolder, setFileExistsInFolder] = useState(false)
  useEffect(() => {
    if (!selectedFolderId || !fileName.trim() || !locationLabel) {
      setFileExistsInFolder(false)
      return
    }
    const timer = setTimeout(() => {
      filesApi.checkConflicts([{ name: fileName.trim(), parentId: selectedFolderId }])
        .then(conflicts => setFileExistsInFolder(conflicts.length > 0))
        .catch(() => setFileExistsInFolder(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [fileName, selectedFolderId, locationLabel])

  // Expected extension from the default filename
  const expectedExt = useMemo(() => {
    const dot = defaultName.lastIndexOf('.')
    return dot > 0 ? defaultName.slice(dot).toLowerCase() : ''
  }, [defaultName])

  const handleConfirm = async () => {
    if (!selectedFolderId || !fileName.trim()) return

    // Check extension mismatch
    if (expectedExt) {
      const currentExt = fileName.lastIndexOf('.') > 0 ? fileName.slice(fileName.lastIndexOf('.')).toLowerCase() : ''
      if (currentExt !== expectedExt) {
        const proceed = await confirm({
          title: 'File extension mismatch',
          message: `The file extension "${currentExt || '(none)'}" doesn't match the expected "${expectedExt}". Save anyway?`,
          type: 'warning',
          confirmText: 'Save anyway',
          cancelText: 'Fix extension',
        })
        if (!proceed) {
          const baseName = fileName.lastIndexOf('.') > 0 ? fileName.slice(0, fileName.lastIndexOf('.')) : fileName
          setFileName(baseName + expectedExt)
          return
        }
      }
    }

    setSaving(true)
    setError(null)
    try {
      // Synchronous source of truth for the destination (see pickedFileRef).
      // Default: the folder the navigator is *currently* showing — never the
      // async-lagged `selectedFolderId`. Exception: if the user's last action
      // was picking a file and the navigator hasn't moved since, honour that
      // file's parent (split-view parent-pane pick, where the destination
      // legitimately differs from the browsed folder).
      const liveFolder = navigatorRef.current?.currentFolderId ?? null
      const pf = pickedFileRef.current
      const destParentId =
        pf && pf.atFolder === liveFolder && pf.parent != null
          ? pf.parent
          : (liveFolder ?? selectedFolderId)
      const trimmedName = fileName.trim()

      // Check for name conflict (skip for link-sheet mode where existing file is intentional)
      // Replace (the default onto an existing name) = a new version of that file.
      let keepBoth = false

      if (!skipConflictCheck) {
        const conflicts = await filesApi.checkConflicts([{ name: trimmedName, parentId: destParentId }])
        if (conflicts.length > 0) {
          // A folder holding the name can't take a version: only Skip / Keep Both. A link (`.chain` /
          // `.shared`) can: Replace writes its new metadata through the link's own rules.
          const hit = conflicts[0]!
          const result = await resolveConflict({
            fileName: trimmedName,
            sourcePath: trimmedName,
            destPath: trimmedName,
            isFolder: !!hit.isFolder,
            isPointer: !!hit.isPointer,
            replaceable: !hit.isFolder,
            operation: 'copy',
            showApplyToAll: false,
          })
          if (result.resolution === 'skip') {
            setSaving(false)
            return
          }
          if (result.resolution === 'keepBoth') {
            keepBoth = true
          }
        }
      }

      const blocked = saveBlockedReason?.()
      if (blocked) {
        setError(blocked)
        setSaving(false)
        return
      }
      // Use custom save (e.g. blob upload for PDF export) or default Y.js copy
      const saved = customSave
        ? await customSave(destParentId, trimmedName, keepBoth)
        : await filesApi.saveAs(fileId!, destParentId, trimmedName, keepBoth)
      // Remember this folder for next time
      setRecentExportFolder(tenantId, destParentId, selectedFolderPath || 'Unknown')
      // …then as the picker tells that folder's path now (the drive's name may
      // have loaded since the path above was reported).
      // (A path that does not end at that folder is a failed lookup, not a better label.)
      navigatorRef.current?.pathOf(destParentId)
        .then(path => { if (path[path.length - 1]?.id === destParentId) setRecentExportFolder(tenantId, destParentId, crumbsText(path)) })
        .catch(() => { /* the path above stands */ })
      onSaved(saved.fileId)
    } catch (err: unknown) {
      // #1397: a damaged stored copy is told in the server's sentence (whatever field the route put it in)
      const text = errorText(err, 'Failed to save')   // #1316: a dropped answer shows nothing
      if (text !== null) setError(damagedMessageFrom(err) ?? text)
      setSaving(false)
    }
  }

  // "Save to" — destination label + folder shortcut buttons. Rendered as
  // its own section between the navigator and the file-name panel.
  const shortcutBtn = (active: boolean) =>
    `text-xs px-2.5 py-1.5 rounded flex items-center gap-1.5 border transition-colors ${
      active
        ? 'bg-blue-600/30 border-blue-500/40 themed-text'
        : 'themed-bg-tertiary themed-border themed-text-secondary hover:themed-text'
    }`
  const saveToSection = (
    <div className="flex items-center flex-wrap gap-2 px-4 py-2.5 border-t themed-border">
      <span className="text-[10px] uppercase themed-text-muted mr-1">
        {typeof locationLabel === 'function' ? locationLabel(fileExistsInFolder) : locationLabel || 'Shortcut'}
      </span>
      {workspaceFolderId && (
        <Tooltip label={`Jump to this ${entityKind === 'miniapp' ? 'PDApp' : 'project'}'s folder`} side="top">
          <button
            onClick={() => { setActiveShortcut('workspace'); navigatorRef.current?.navigateTo(workspaceFolderId) }}
            className={shortcutBtn(activeShortcut === 'workspace' && selectedFolderId === workspaceFolderId)}
          >
            <LayoutGrid size={13} className="text-purple-400" />
            This {entityKind === 'miniapp' ? 'PDApp' : 'Project'}
          </button>
        </Tooltip>
      )}
      {/* Source Folder — one-click jump to the drive root, always. */}
      {sourceFolderId && (
        <Tooltip label="Jump to the drive root (papan)" side="top">
          <button
            onClick={() => { setActiveShortcut('source'); navigatorRef.current?.navigateTo(sourceFolderId) }}
            className={shortcutBtn(activeShortcut === 'source' && selectedFolderId === sourceFolderId)}
          >
            <FolderOpen size={13} className="text-yellow-500" />
            Source Folder
          </button>
        </Tooltip>
      )}
      {/* Last folder the user saved into — persisted in localStorage by
          handleConfirm. Shown whenever there's a recorded one. */}
      {recent && (
        <Tooltip label={recent.folderName ? `Last saved in ${recent.folderName}` : 'Last folder you saved into'} side="top">
          <button
            onClick={() => { setActiveShortcut('recent'); navigatorRef.current?.navigateTo(recent.folderId) }}
            className={shortcutBtn(activeShortcut === 'recent' && selectedFolderId === recent.folderId)}
          >
            <Clock size={13} />
            Last Folder
          </button>
        </Tooltip>
      )}
    </div>
  )

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-[600px] h-[560px] max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <div className="flex items-center gap-2">
            <Save size={16} className="themed-text-secondary" />
            <h3 className="text-sm font-medium themed-text">{title || 'Save As'}</h3>
          </div>
          <Tooltip label="Close">
            <button onClick={onClose} aria-label="Close" className="themed-text-muted hover:themed-text">
              <X size={16} />
            </button>
          </Tooltip>
        </div>

        {/* Description */}
        {description && (
          <p className="px-4 pt-2.5 pb-2 text-xs themed-text-secondary">{description}</p>
        )}

        {/* Destination navigator — Back/Forward/Up + breadcrumb + search
            toolbar, then the two-pane (parent | current) browser. */}
        <PickerNavigator
          ref={navigatorRef}
          roots={roots}
          initialFolderId={effectiveDefaultId}
          mode="fileOrFolder"
          selectedId={selectedId}
          onSelect={handleSelect}
          onNavigate={handleNavigate}
          decorateItem={decorateItem}
          autoFocusSearch={false}
          splitView
          className="flex-1"
        />

        {/* "Save to" — its own section on the modal background, kept
            separate from (and a different colour to) the file-name panel. */}
        {saveToSection}

        {/* File-name panel — distinct background, the bottom band of a
            native Save dialog. */}
        <div className="themed-bg-tertiary border-t themed-border">
          <div className="flex items-center gap-2 px-4 pt-3 pb-2">
            <label htmlFor="saveas-filename" className="text-xs themed-text-secondary shrink-0">
              File name
            </label>
            <input
              id="saveas-filename"
              ref={inputRef}
              type="text"
              value={fileName}
              onChange={e => setFileName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleConfirm() }}
              className="flex-1 min-w-0 text-sm themed-bg-secondary themed-text rounded px-2.5 py-1.5 border themed-border outline-none focus:border-brand-500 transition-colors"
            />
          </div>
          <div className="flex items-center justify-between px-4 pb-3">
            {/* Status — error / read-only warnings only. The destination
                path lives in the navigator's breadcrumb, not here. */}
            <div className={`text-xs max-w-[300px] ${error ? 'whitespace-normal break-words' : 'truncate'}`}>
              {error ? <span className="text-red-400">{error}</span>
                : selectedFileReadOnly ? <span className="text-amber-400">Read-only file</span>
                : !folderWritable ? (
                  <span className="text-amber-400">
                    Read-only folder
                    {writableSuggestion && (
                      <> — <button className="underline hover:text-amber-300" onClick={() => navigatorRef.current?.navigateTo(writableSuggestion.parentId || writableSuggestion.id)}>
                        go to {writableSuggestion.name || 'writable folder'}
                      </button></>
                    )}
                  </span>
                )
                : null}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!selectedFolderId || !fileName.trim() || saving || !folderWritable || selectedFileReadOnly}
                className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {saving && <Loader2 size={12} className="animate-spin" />}
                {typeof confirmLabel === 'function' ? confirmLabel(fileExistsInFolder) : confirmLabel || 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
